<template>
  <v-card flat>
    <div
      id="dnd-file-area"
      class="dnd-file-area"
      :class="{hover: hover, disabled: disabled}"
      :style="{
        opacity: disabled ? 0.5 : 1.0,
        'min-height': more ? '100px' : '240px',
      }"
      :title="$t('dropFilesHere')"
    >
      <v-layout
        class="inner-layout"
        justify-center
        align-center
        @click="disabled ? null : browse()"
      >
        <v-layout
          column
          align-center
        >
          <v-icon
            v-if="!more"
            class="upload-icon mb-2"
          >
            cloud_upload
          </v-icon>
          <span
            class="title"
            :class="{
              'grey--text text--darken-3': !hover || disabled,
              'msr--text': hover && !disabled,
            }"
          >{{ hint }}</span>
          <div class="mt-2 subheading grey--text text--darken-2">
            {{ $t('or') }}
            <a
              href="javascript:void(0)"
              :class="{'grey--text': disabled}"
            >{{
              $t('selectFile')
            }}</a>
          </div>
          <slot name="extra" />
        </v-layout>
      </v-layout>
    </div>
  </v-card>
</template>

<script>
export default {
  props: {
    single: {
      type: Boolean,
      default: false,
    },
    more: {
      type: Boolean,
      default: false,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      dragCounter: 0,
      eventHandlers: {},
    };
  },
  computed: {
    /**
     * Whether the drop area is being hovered.
     *
     * Based on a counter to avoid flickering while hovering child
     * elements of the parent drop area.
     *
     * See: https://stackoverflow.com/a/21002544
     *
     */
    hover() {
      return this.dragCounter > 0;
    },
    hint() {
      if (this.single) {
        return this.$t('dragAndDropHintSingular');
      }
      if (this.more) {
        return this.$t('dragAndDropMoreHint');
      }
      return this.$t('dragAndDropHint');
    },
  },
  mounted() {
    this.$nextTick(() => {
      this.registerEvents();
    });
  },
  beforeDestroy() {
    this.deregisterEvents();
  },
  methods: {
    browse() {
      this.$emit('browse');
    },
    onFilesDropped(files) {
      this.$emit('dropped', files);
    },
    getArea() {
      return document.querySelector('#dnd-file-area');
    },
    registerEvents() {
      this.deregisterEvents();

      const vm = this;

      function onDragEnter(e) {
        vm.dragCounter += 1;
      }

      function onDragLeave(e) {
        e.preventDefault();
        vm.dragCounter -= 1;
      }

      function onDragOver(e) {
        e.preventDefault();
      }

      function onDrop(e) {
        e.preventDefault();
        vm.dragCounter -= 1;

        const files = e.dataTransfer.files;
        vm.onFilesDropped(files);
      }

      this.eventHandlers = {};
      this.eventHandlers.dragenter = onDragEnter;
      this.eventHandlers.dragleave = onDragLeave;
      this.eventHandlers.dragover = onDragOver;
      this.eventHandlers.drop = onDrop;

      const area = this.getArea();
      area.addEventListener('dragenter', onDragEnter);
      area.addEventListener('dragleave', onDragLeave);
      area.addEventListener('dragover', onDragOver);
      area.addEventListener('drop', onDrop);
    },
    deregisterEvents() {
      this.dragCounter = 0;
      if (!this.eventHandlers) {
        return;
      }

      const area = this.getArea();
      if (!area) {
        return;
      }
      area.removeEventListener('dragenter', this.eventHandlers.dragenter);
      area.removeEventListener('dragleave', this.eventHandlers.dragleave);
      area.removeEventListener('dragover', this.eventHandlers.dragover);
      area.removeEventListener('drop', this.eventHandlers.drop);

      this.eventHandlers = {};
    },
  },
};
</script>

<style scoped>
.upload-icon {
  font-size: 6em;
  color: rgba(0, 0, 0, 0.12);
}
.upload-icon.small {
  font-size: 4em;
}

.dnd-file-area {
  cursor: pointer;
  width: 100%;
  background: rgba(0, 0, 0, 0.01);
  border: 6px dashed rgba(0, 0, 0, 0.1);
  border-radius: 16px !important;
}
.dnd-file-area:not(.disabled).hover {
  border-color: var(--msr-color-primary);
}
.dnd-file-area:not(.disabled).hover .upload-icon {
  color: var(--msr-color-primary);
}

.inner-layout {
  min-height: inherit;
}
</style>



// WEBPACK FOOTER //
// src/components/DragAndDropFileArea.vue